import { OrderStatus } from '@customTypes/order';
import { BaseRequisition, BaseRequisitionOrder, CaseRequisition, Requisition } from '@customTypes/requisition';
import { TIMELINE_STATUS, TimelineStatus, TimelineStep, TimelineStepId } from '@customTypes/timeline';
import { PRODUCT_ID_DATA_ONLY } from '@constants/constants';
import { formatDateForDisplay } from '@helpers/date';
import { isOrderComplete } from '@helpers/order';
import { isClinicianReviewSkipped, isFamilyMemberWithoutSecondaryFindings } from '@helpers/requisition';

/**
 * Timeline date fields used to show dates under completed steps.
 * All optional so both Requisition and CaseRequisition can be passed.
 */
interface TimelineDateFields {
  order_date?: string;
  sample_received_at?: string | null;
  first_reported_at?: string | null;
  clinical_summary_letter_ready_at?: string | null;
}

/**
 * Gets the "Complete" step date: CSL date if present, otherwise reported date
 */
function getCompleteStepDate(fields: TimelineDateFields): string | undefined {
  if (fields.clinical_summary_letter_ready_at) {
    return formatDateForDisplay(fields.clinical_summary_letter_ready_at);
  }
  return formatDateForDisplay(fields.first_reported_at ?? undefined);
}

/**
 * Known external status values
 */
const KNOWN_STATUSES = [
  'AWAITING_SAMPLE',
  'ANALYZING',
  'REFERRAL_LAB_LD_REVIEW',
  'REPORTED',
  'CLINICIAN_REVIEW',
  'COMPLETED',
  'CANCELED',
  'FAILED',
] as const;

/**
 * External status type for requisition external_status field
 */
type ExternalStatus = (typeof KNOWN_STATUSES)[number];

/**
 * Type guard to check if a value is a valid ExternalStatus
 */
function isExternalStatus(status: unknown): status is ExternalStatus {
  return KNOWN_STATUSES.includes(status as ExternalStatus);
}

/**
 * Timeline step definition with ordering
 */
interface ITimelineStepDefinition {
  id: TimelineStepId;
  title: string;
  description: string;
  order: number; // Order in the workflow (1 = first, 2 = second, etc.)
  // Optional: status overrides for specific external_statuses
  statusOverrides?: Partial<Record<ExternalStatus, TimelineStatus>>;
  // Optional: secondary text configuration
  secondaryTextConfig?: {
    // Show "In progress" for these statuses
    inProgressStatuses?: readonly ExternalStatus[];
  };
}

/**
 * Timeline steps for the order status timeline
 * These steps are decoupled from ORDER_PROCESS_STEPS to allow independent evolution
 */
export const TIMELINE_STEPS = [
  {
    id: 'ordered',
    title: 'Order Received',
    description: "We have received the order and are awaiting the patient's sample.",
    order: 1,
  },
  {
    id: 'sample-received',
    title: 'Sample Received',
    description: 'The sample has arrived and is being prepared for processing in the lab.',
    order: 2,
  },
  {
    id: 'lab',
    title: 'Lab Processing',
    description:
      'The sample is undergoing lab processing and analysis. Most results are delivered within 5-6 weeks from the sample received date.',
    order: 3,
    statusOverrides: {
      ANALYZING: TIMELINE_STATUS.IN_PROGRESS,
    },
    secondaryTextConfig: {
      inProgressStatuses: ['ANALYZING'],
    },
  },
  {
    id: 'reported',
    title: 'Reported',
    description: 'Results are ready and available for download.',
    order: 4,
  },
  {
    id: 'clinician-review',
    title: 'Clinical Summary Ready',
    description:
      "Our third-party partner is reviewing the results and generating a clinical summary letter. You'll be notified when it is complete.",
    order: 5,
    statusOverrides: {
      CLINICIAN_REVIEW: TIMELINE_STATUS.IN_PROGRESS,
    },
    secondaryTextConfig: {
      inProgressStatuses: ['CLINICIAN_REVIEW'],
    },
  },
  {
    id: 'completed',
    title: 'Complete',
    description: 'All results and associated documents are available for download.',
    order: 6,
  },
] as const satisfies readonly ITimelineStepDefinition[];

/**
 * Maps external status to the step order that should be completed
 * When a status is reached, all steps with order <= the mapped value are complete
 */
const STATUS_TO_STEP_ORDER: Partial<Record<ExternalStatus, number>> = {
  AWAITING_SAMPLE: 1, // Only "Ordered" is complete
  ANALYZING: 2, // "Ordered" + "Sample Received" complete; "Lab Processing" is set to in-progress via statusOverrides
  REFERRAL_LAB_LD_REVIEW: 3, // "Ordered", "Sample Received", and "Lab Processing" are complete
  CLINICIAN_REVIEW: 4, // All steps up to "Reported" (order 4) are complete; "Clinical Summary Ready" (order 5) is set to in-progress via statusOverrides
  COMPLETED: 6, // All steps are complete including "Complete" (order 6)
  REPORTED: 6, // All steps are complete including "Complete" (order 6)
} as const;

/**
 * Module-level lookup maps for performance
 */
const STEP_ORDERS: Record<TimelineStepId, number> = TIMELINE_STEPS.reduce(
  (acc, step) => {
    acc[step.id] = step.order;
    return acc;
  },
  {} as Record<TimelineStepId, number>,
);

const STEP_DEFINITION_BY_ID: Record<TimelineStepId, ITimelineStepDefinition> = TIMELINE_STEPS.reduce(
  (acc, step) => {
    acc[step.id] = step;
    return acc;
  },
  {} as Record<TimelineStepId, ITimelineStepDefinition>,
);

/**
 * Gets reported step metrics (total parts and completed parts)
 * Excludes canceled and failed orders from the total count.
 * Counts orders with CLINICIAN_REVIEW, REPORTED, or COMPLETED status as completed.
 * @param requisition - The requisition to calculate metrics for
 * @returns Object with totalParts and completedParts
 */
function getReportedStepMetrics(requisition: BaseRequisition | CaseRequisition): {
  totalParts: number;
  completedParts: number;
} {
  let totalParts = 0;
  let completedParts = 0;

  for (const order of requisition.orders) {
    // Skip DATA_ONLY orders
    if (order.product_id === PRODUCT_ID_DATA_ONLY) {
      continue;
    }

    // Exclude canceled and failed orders from the total count
    if (order.status === OrderStatus.CANCELED || order.status === OrderStatus.FAILED) {
      continue;
    }

    totalParts += 1;

    // Count orders with CLINICIAN_REVIEW, REPORTED, or COMPLETED status as completed
    if (isOrderComplete(order)) {
      completedParts += 1;
    }
  }

  return { totalParts, completedParts };
}

/**
 * Creates base timeline steps with all steps incomplete
 */
function createBaseTimelineSteps(requisition: BaseRequisition | CaseRequisition): TimelineStep[] {
  const hasClinicianReview = !isClinicianReviewSkipped(requisition);
  const familyMemberWithoutSecondaryFindings =
    'case_requisitions' in requisition ? isFamilyMemberWithoutSecondaryFindings(requisition as Requisition) : false;

  // Check if all orders are data-only
  const hasOnlyDataOnlyOrders =
    requisition.orders.length > 0 && requisition.orders.every(order => order.product_id === PRODUCT_ID_DATA_ONLY);

  return TIMELINE_STEPS.filter(step => {
    // Exclude clinician-review if hasClinicianReview is false
    if (step.id === 'clinician-review' && !hasClinicianReview) {
      return false;
    }
    // Exclude reported step for family members without secondary findings
    if (step.id === 'reported' && familyMemberWithoutSecondaryFindings) {
      return false;
    }
    // Exclude reported step if all orders are data-only
    if (step.id === 'reported' && hasOnlyDataOnlyOrders) {
      return false;
    }
    return true;
  }).map(step => {
    return {
      id: step.id,
      label: step.title,
      tooltipText: step.description,
      status: TIMELINE_STATUS.INCOMPLETE,
      secondaryText: undefined,
    };
  });
}

/**
 * Checks if there's at least one reportable order that is completed
 * This handles both single and multiple order cases, and determines whether
 * the 'Reported' step should show progress/completion
 * @param orders - Array of orders to check
 * @returns True if there's at least one reportable order that is COMPLETED
 */
function hasAtLeastOneReportableOrderCompleted(orders: BaseRequisitionOrder[]): boolean {
  const reportableOrders = orders.filter(order => order.product_id !== PRODUCT_ID_DATA_ONLY);
  return reportableOrders.length > 0 && reportableOrders.some(isOrderComplete);
}

interface StatusStepsOptions {
  status: ExternalStatus;
  baseSteps: TimelineStep[];
  reportedMetrics: {
    completedParts: number;
    totalParts: number;
  };
  requisition: BaseRequisition | CaseRequisition;
}

/**
 * Builds timeline steps for a given status
 * Uses step ordering to determine which steps should be complete.
 * When a step is complete and has an associated date, shows the formatted date under the label (except Lab Processing).
 */
function buildStepsForStatus({ status, baseSteps, reportedMetrics, requisition }: StatusStepsOptions): TimelineStep[] {
  const completedStepOrder = STATUS_TO_STEP_ORDER[status] ?? 0;
  const orders = requisition.orders;
  const dateFields = requisition as TimelineDateFields;
  const hasAtLeastOneReportableCompleted = hasAtLeastOneReportableOrderCompleted(orders);

  return baseSteps.map(step => {
    const stepOrder = STEP_ORDERS[step.id] ?? 0;
    const stepDefinition = STEP_DEFINITION_BY_ID[step.id];

    let stepStatus: TimelineStatus = TIMELINE_STATUS.INCOMPLETE;
    let secondaryText: string | undefined;

    // Special case: Sample Received should be incomplete for AWAITING_SAMPLE
    // even though it's order 2 and AWAITING_SAMPLE maps to order 1
    if (step.id === 'sample-received') {
      stepStatus =
        status !== 'AWAITING_SAMPLE' && stepOrder <= completedStepOrder
          ? TIMELINE_STATUS.COMPLETE
          : TIMELINE_STATUS.INCOMPLETE;
    }
    // Special case: Lab Processing should be complete if there's at least one reportable order that is COMPLETED
    // (handles both single and multiple order cases). No date shown per product decision.
    else if (step.id === 'lab') {
      if (hasAtLeastOneReportableCompleted) {
        stepStatus = TIMELINE_STATUS.COMPLETE;
      } else if (stepDefinition?.statusOverrides?.[status]) {
        stepStatus = stepDefinition.statusOverrides[status]!;
        // Set secondary text if this status should show "In progress"
        if (stepDefinition.secondaryTextConfig?.inProgressStatuses?.includes(status)) {
          secondaryText = 'In progress';
        }
      } else {
        stepStatus = stepOrder <= completedStepOrder ? TIMELINE_STATUS.COMPLETE : TIMELINE_STATUS.INCOMPLETE;
      }
    }
    // Special case: Reported step uses dedicated function
    else if (step.id === 'reported') {
      const reportedStatus = getReportedStepStatus(
        status,
        reportedMetrics.totalParts,
        reportedMetrics.completedParts,
        hasAtLeastOneReportableCompleted,
      );
      stepStatus = reportedStatus.stepStatus;
      secondaryText = reportedStatus.secondaryText;
    }
    // Use declarative status overrides if defined
    else if (stepDefinition?.statusOverrides?.[status]) {
      stepStatus = stepDefinition.statusOverrides[status]!;

      // Set secondary text if this status should show "In progress"
      if (stepDefinition.secondaryTextConfig?.inProgressStatuses?.includes(status)) {
        secondaryText = 'In progress';
      }
    }
    // For all other steps, use the step order logic
    else {
      stepStatus = stepOrder <= completedStepOrder ? TIMELINE_STATUS.COMPLETE : TIMELINE_STATUS.INCOMPLETE;
    }

    // When step is complete, set date for under-dot (desktop) and under-label (mobile). Except Lab Processing.
    let dateText: string | undefined;
    if (stepStatus === TIMELINE_STATUS.COMPLETE && step.id !== 'lab') {
      switch (step.id) {
        case 'ordered':
          dateText = formatDateForDisplay(dateFields.order_date);
          break;
        case 'sample-received':
          dateText = formatDateForDisplay(dateFields.sample_received_at);
          break;
        case 'reported':
          dateText = formatDateForDisplay(dateFields.first_reported_at ?? undefined);
          break;
        case 'clinician-review':
          dateText = formatDateForDisplay(dateFields.clinical_summary_letter_ready_at ?? undefined);
          break;
        case 'completed':
          dateText = getCompleteStepDate(dateFields);
          break;
        default:
          dateText = undefined;
      }
    }

    return {
      ...step,
      status: stepStatus,
      secondaryText,
      dateText,
    };
  });
}

/**
 * Statuses where reports can be ready
 */
const STATUSES_WITH_REPORTS_READY: readonly ExternalStatus[] = ['COMPLETED', 'REPORTED', 'CLINICIAN_REVIEW'];

/**
 * Checks if a status can have reports ready
 */
function canHaveReportsReady(status: ExternalStatus): boolean {
  return STATUSES_WITH_REPORTS_READY.includes(status);
}

/**
 * Gets the reported step status and secondary text based on external status and metrics
 * @param status - The external status of the requisition
 * @param totalParts - Total number of report parts
 * @param completedParts - Number of completed report parts
 * @param hasAtLeastOneReportableCompleted - Whether there's at least one reportable order that is completed
 * @returns Object with stepStatus and secondaryText
 */
function getReportedStepStatus(
  status: ExternalStatus,
  totalParts: number,
  completedParts: number,
  hasAtLeastOneReportableCompleted: boolean,
): { stepStatus: TimelineStatus; secondaryText: string | undefined } {
  // If no reportable parts, return incomplete with no secondary text
  if (totalParts === 0) {
    return {
      stepStatus: TIMELINE_STATUS.INCOMPLETE,
      secondaryText: undefined,
    };
  }

  // Calculate completed parts for statuses where reports can be ready,
  // or if there's at least one reportable order that is completed (handles both single and multiple order cases)
  let actualCompletedParts = 0;
  if (canHaveReportsReady(status) || hasAtLeastOneReportableCompleted) {
    actualCompletedParts = Math.min(totalParts, completedParts);
  }

  let stepStatus: TimelineStatus = TIMELINE_STATUS.INCOMPLETE;
  if (actualCompletedParts === totalParts) {
    stepStatus = TIMELINE_STATUS.COMPLETE;
  } else if (actualCompletedParts > 0) {
    stepStatus = TIMELINE_STATUS.IN_PROGRESS;
  }

  // Show "X of Y" format for all cases where totalParts > 0
  const secondaryText = `${actualCompletedParts} of ${totalParts}`;

  return { stepStatus, secondaryText };
}

/**
 * Maps external_status to timeline steps
 * @param requisition - The requisition to generate timeline steps for
 */
export function getTimelineStepsFromStatus(requisition: BaseRequisition | CaseRequisition): TimelineStep[] {
  const { external_status: rawStatus } = requisition;
  const baseSteps = createBaseTimelineSteps(requisition);

  if (!rawStatus || !isExternalStatus(rawStatus)) {
    return baseSteps;
  }

  if (rawStatus === OrderStatus.CANCELED || rawStatus === OrderStatus.FAILED) {
    return baseSteps;
  }

  // When the requisition is ineligible for clinician review, treat a CLINICIAN_REVIEW
  // external status as COMPLETED for display purposes so the timeline lights up the
  // final step instead of leaving it incomplete.
  const status =
    rawStatus === OrderStatus.CLINICIAN_REVIEW && isClinicianReviewSkipped(requisition)
      ? OrderStatus.COMPLETED
      : rawStatus;

  if (!STATUS_TO_STEP_ORDER[status]) {
    return baseSteps;
  }

  const reportedMetrics = getReportedStepMetrics(requisition);
  return buildStepsForStatus({
    status,
    baseSteps,
    reportedMetrics,
    requisition,
  });
}

// Extract timeline step labels as constants for reuse
const REPORTED_LABEL = TIMELINE_STEPS.find(step => step.id === 'reported')?.title ?? 'Reported';
const COMPLETE_LABEL = TIMELINE_STEPS.find(step => step.id === 'completed')?.title ?? 'Complete';

/**
 * Determines the status label for a requisition with REPORTED status.
 * Returns "Complete" if all reportable orders are COMPLETED, otherwise "Reported".
 * @param requisition - The requisition to check
 * @returns "Complete" or "Reported" based on order completion status
 */
export function getReportedStatusLabel(requisition: BaseRequisition | CaseRequisition): string {
  const reportableOrders = requisition.orders?.filter(order => order.product_id !== PRODUCT_ID_DATA_ONLY) ?? [];

  // No reportable orders, default to "Reported"
  if (reportableOrders.length === 0) {
    return REPORTED_LABEL;
  }

  // Check if all reportable orders are COMPLETED
  const allComplete = reportableOrders.every(order => order.status === OrderStatus.COMPLETED);
  return allComplete ? COMPLETE_LABEL : REPORTED_LABEL;
}

/**
 * Finds the last step with progress (complete or in-progress) from timeline steps.
 * Falls back to the first step if no progress is found.
 * @param steps - The timeline steps to search
 * @returns The label of the last step with progress, or the first step's label, or null
 */
export function findLastStepWithProgress(steps: TimelineStep[]): string | null {
  // Find the last step with progress (complete or in-progress)
  for (let i = steps.length - 1; i >= 0; i -= 1) {
    const step = steps[i];
    if (step.status === TIMELINE_STATUS.COMPLETE || step.status === TIMELINE_STATUS.IN_PROGRESS) {
      return step.label;
    }
  }

  // If no step has progress, return the first step's label (Order Received)
  return steps.length > 0 ? steps[0].label : null;
}
